import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
} from '@mui/material';
import { RestartAlt, CheckCircle, Warning, Info } from '@mui/icons-material';
import Step1ChooseCarrier from './Step1ChooseCarrier';
import Step2UploadExcel from './Step2UploadExcel';
import Step3InvoicePreview from './Step3InvoicePreview';
import { ParsedRow } from './types';
import Title from '@Components/Title';
import Image from '@Components/Image';
import { useLocation, useNavigate } from 'react-router-dom';
import SbButton from '@Components/Button';
import { EncryptedPayload } from '../BulkPurchaseInvoice/types';

const steps = [
  {
    label: 'Choose Carrier',
    description: 'Select your logistics provider',
    icon: '🏢',
  },
  {
    label: 'Upload Excel',
    description: 'Upload and validate your invoice data',
    icon: '📊',
  },
  {
    label: 'Invoice Preview',
    description: 'Review and submit your invoice',
    icon: '👁️',
  },
];

export default function BulkDebitPurchaseInvoiceFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [encryptedPayload, setEncryptedPayload] = useState<EncryptedPayload>({
    encrypted: '',
    iv: '',
    authTag: '',
  });

  useEffect(() => {
    if (location?.state?.carrierId && location?.state?.shipments) {
      setSelectedCarrier(location?.state?.carrierId);
      setActiveStep(1);
    }
  }, [location]);

  const theme = useTheme();

  const handleNext = () => {
    // setLastCompletedStep(activeStep);
    setActiveStep(prev => prev + 1);

    // Show completion dialog when finishing the flow
    if (activeStep === steps.length - 1) {
      setCompletionDialogOpen(true);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      navigate('/purchase-invoices/debit-creation', {
        state: {
          carrierId: location?.state?.carrierId,
          shipments: location?.state?.shipments,
        },
      });
    } else {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedCarrier('');
    setRows([]);
    setResetDialogOpen(false);
  };

  const handleCarrierSelect = (carrierId: string) => {
    setSelectedCarrier(carrierId);
  };

  const handleDataParsed = (data: ParsedRow[]) => {
    setRows(data);
  };

  const canProceedToNextStep = () => {
    switch (activeStep) {
      case 0:
        return selectedCarrier !== '';
      case 1:
        return rows.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <Box
          onClick={() => {
            if (activeStep === 0) {
              navigate('/purchase-invoices');
            } else if (activeStep === 1) {
              setActiveStep(0);
            } else {
              setActiveStep(1);
            }
          }}
          sx={{ zIndex: '1', cursor: 'pointer' }}
        >
          <Image src="/images/backbutton.svg" alt="Back Button" style={{ width: '1.5rem', marginTop: '0.2rem' }} />
        </Box>
        <Title title="Purchase Invoices" subTitle="View and create purchase invoices" />
      </Box>
      <Box sx={{ marginY: '2rem' }}>
        <Title title="Create Invoices" removeBorder removePadding titleClass="underline px_20-text" />
      </Box>

      {/* Main Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && (
          <Step1ChooseCarrier selectedCarrier={selectedCarrier} onChange={handleCarrierSelect} onNext={handleNext} />
        )}

        {activeStep === 1 && (
          <Step2UploadExcel
            carrierId={selectedCarrier}
            onParsedData={handleDataParsed}
            onNext={handleNext}
            onBack={handleBack}
            setEncryptedPayload={setEncryptedPayload}
          />
        )}

        {activeStep === 2 && (
          <Step3InvoicePreview
            carrierId={selectedCarrier}
            parsedData={rows}
            onReset={handleReset}
            onBack={handleBack}
            encryptedPayload={encryptedPayload}
          />
        )}
      </Box>

      {/* Global Navigation */}
      {activeStep < 2 && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            pt: 2,
            gap: 5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: { xs: '100%', sm: '40%', md: '30%' },
              maxWidth: 400,
              justifyContent: 'flex-end',
            }}
          >
            {activeStep > 0 && (
              <SbButton
                className="rounded-full"
                onClick={handleBack}
                variant="outlined"
                size="large"
                disabled={activeStep === 0}
                fullWidth
                sx={{ maxWidth: '200px' }}
              >
                Back
              </SbButton>
            )}
          </Box>

          <SbButton
            variant="contained"
            className="rounded-full"
            onClick={handleNext}
            disabled={!canProceedToNextStep()}
            size="large"
            fullWidth
            sx={{
              minWidth: 120,
              maxWidth: '200px',
              background: !canProceedToNextStep()
                ? theme.palette.action.disabledBackground
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: !canProceedToNextStep() ? theme.palette.text.disabled : '#fff',
              '&:disabled': {
                background: theme.palette.action.disabledBackground,
                color: theme.palette.text.disabled,
              },
            }}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </SbButton>
        </Box>
      )}

      {/* Help Alert */}
      {activeStep === 0 && !selectedCarrier && (
        <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
          <Typography variant="body2">
            <strong>Getting Started:</strong> Select a carrier to begin creating your bulk purchase invoice. Make sure
            you have your Excel file ready with shipment data.
          </Typography>
        </Alert>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Reset Progress?
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to reset the entire workflow? This will clear:</Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            {selectedCarrier && <li>Selected carrier</li>}
            {rows.length > 0 && <li>{rows.length} uploaded shipments</li>}
            <li>All current progress</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <SbButton onClick={() => setResetDialogOpen(false)}>Cancel</SbButton>
          <Button onClick={handleReset} color="warning" variant="contained" startIcon={<RestartAlt />}>
            Reset Everything
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onClose={() => setCompletionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Invoice Created Successfully!
        </DialogTitle>
        <DialogContent>
          <Typography>Your bulk purchase invoice has been created and submitted successfully.</Typography>
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark">
              <strong>Summary:</strong>
            </Typography>
            <Typography variant="body2" color="success.dark">
              • Carrier: {selectedCarrier}
            </Typography>
            <Typography variant="body2" color="success.dark">
              • Shipments Processed: {rows.length}
            </Typography>
            <Typography variant="body2" color="success.dark">
              • Status: Submitted for processing
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <SbButton
            onClick={() => {
              setCompletionDialogOpen(false);
              handleReset();
            }}
            variant="contained"
            color="success"
          >
            Create Another Invoice
          </SbButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
